import moment from "moment"
import { STOCK_TYPES, getEventStockType } from "./stockTypes"

/**
 * KFC-352: the three categories the ticket names, in the order it names them —
 * "rangés par catégorie de produits (E / P / D)".
 */
const LEADING_PRODUCT_TYPES = ["STARTER", "MAIN_COURSE", "DESSERT"]

/**
 * A count expires, and completeness expires with it.
 *
 * Past `INVENTORY_FRESH_HOURS` a fridge reads as never counted — for the tile colour, for a
 * partial cell, and for the transfer guard alike. Counting one fridge therefore never signs off
 * a pass taken on the other one hours earlier, and the next day starts from a clean slate.
 *
 * The two fridges can still be counted in two passes, but both must fall inside that delay.
 */

/** KFC-352: the three positions of the fridge filter, for hubs with a retail offer. */
export const FRIDGE_FILTERS = {
	ALL: "ALL",
	RETAIL: "RETAIL",
	CLICK_AND_COLLECT: "CLICK_AND_COLLECT"
}

/**
 * A stock of 0 is a declared value; an absent one is not. Products received before the
 * two-fridge split carry no `volumeLibreService` at all, which is what the "–" cell stands
 * for.
 */
export const isStockDeclared = (volume) => volume !== undefined && volume !== null

/**
 * KFC-352: whether a product has a stock declared for the fridge currently being looked at.
 *
 * In single-fridge view the prototype does not grey the tile out, it removes it: switching to
 * "Click & collect" makes every tile without a click & collect stock disappear. This is
 * therefore a filtering rule, and it has to run before the tiles are grouped — otherwise a
 * letter row, or a whole category band, emptied of all its tiles would still render its
 * header.
 */
export function hasDeclaredStockFor(productStock, fridgeFilter) {
	if (FRIDGE_FILTERS.RETAIL === fridgeFilter) return isStockDeclared(productStock.volumeLibreService)
	if (FRIDGE_FILTERS.CLICK_AND_COLLECT === fridgeFilter) return isStockDeclared(productStock.volume)

	return true
}

/**
 * KFC-352: category order on the inventory screen.
 *
 * Until this ticket there was no order at all: `Object.keys()` returned the categories in
 * insertion order, which follows whatever the Parse query happened to return, so the screen
 * reordered itself as the data changed.
 *
 * The ticket names only three categories out of the 28 in the referential, and a hub commonly
 * shows five or six. The three named ones therefore lead, and everything else follows the
 * `order` of `ProductTypes`, which is the referential the rest of the tool already uses. A
 * category absent from the referential lands last, alphabetically, so the result never
 * depends on the input order.
 *
 * @param productTypes the full `ProductTypes` referential — `{ kfcName, label, order }`. Not
 *        `productTypeOptions`, which is filtered on managementMode and drops every "manual"
 *        category, Boisson included.
 */
export function sortProductTypeKeys(productTypeKeys, productTypes = []) {
	const orderByType = new Map(productTypes.map(productType => [productType.kfcName, productType.order]))

	const rank = (productType) => {
		const leading = LEADING_PRODUCT_TYPES.indexOf(productType)
		if (-1 !== leading) return leading

		const order = orderByType.get(productType)

		return order === undefined || order === null
			? Number.MAX_SAFE_INTEGER
			: LEADING_PRODUCT_TYPES.length + order
	}

	return [...productTypeKeys].sort((a, b) => {
		const rankA = rank(a)
		const rankB = rank(b)

		return rankA !== rankB ? rankA - rankB : String(a).localeCompare(String(b))
	})
}

export function formatInventoryData(receptionData) {
	const newReceptionData = {}

	receptionData.forEach(elm => {
		const group = elm.uniqueCode.replace(/\d/g,"")

		if (!newReceptionData[elm.productType]) newReceptionData[elm.productType] = {}
		if (!newReceptionData[elm.productType][group])  newReceptionData[elm.productType][group] = []
		newReceptionData[elm.productType][group].push(elm)
	})
	
	return newReceptionData
}

/**
 * KFC-285: the last inventory recorded for one fridge.
 *
 * Events written before the two-fridge split carry no `stockType`; `getEventStockType` reads
 * them as click & collect, which is what they described when it was the only stock tracked.
 */
export function lastInventoryFor(productStock, stockType) {
	return lastInventoryOf({
		events: (productStock.events || []).filter(event => getEventStockType(event) === stockType)
	})
}

/** The most recent inventory of the product, whichever fridge it was taken on. */
export function lastInventoryOf(productStock) {
	// The most recent by date, not the last in the array: nothing guarantees the events are
	// stored in order, and this event's date is what the hover text shows.
	return (productStock.events || [])
		.filter(event => "INVENTORY" === event.type)
		.reduce((latest, event) => (
			!latest || moment.utc(event.date).isAfter(moment.utc(latest.date)) ? event : latest
		), null)
}

/**
 * Who counted a fridge and when, as the tile's hover text.
 *
 * A grey tile names its last count whatever its age, since an empty fridge stays empty. Any other
 * tile names a count only while it still colours the tile: past the freshness delay the tile is
 * white again and asks to be counted, so it names nobody.
 */
export function inventoryLabel(event, empty = false) {
	if (!event) return ""

	const stamp = `${event.user?.username ?? "unknown"} - ${moment(event.date).format("DD/MM HH:mm")}`

	if (empty) return stamp

	return isInventoryFresh(event) ? `Inventaire fait - ${stamp}` : ""
}

/**
 * How long a count keeps a tile coloured.
 *
 * The inventory is redone every day, so a count taken this morning must not still show as done
 * this afternoon: past this delay the tile goes back to white and asks to be counted again.
 * This is what the single-stock screen has always done through `checked`.
 */
export const INVENTORY_FRESH_HOURS = 2

/** Whether that inventory event is recent enough to still count. */
export function isInventoryFresh(event, now = moment.utc()) {
	if (!event) return false

	return moment.duration(now.diff(moment.utc(event.date))).asHours() < INVENTORY_FRESH_HOURS
}

/**
 * Whether that fridge holds a count that still stands.
 *
 * A count expires with the same delay everywhere: past it the fridge counts as never counted, so
 * the counter manager can inventory again the next day without the previous day's count standing
 * in the way — of the tile colour, of a partial cell, or of a transfer.
 */
export function isFridgeCounted(productStock, stockType) {
	return isInventoryFresh(lastInventoryFor(productStock, stockType))
}

/**
 * KFC-285: whether every fridge that holds a stock has been counted.
 *
 * A fridge whose stock was never declared — the "–" cell — does not hold the product back:
 * counting the self-service side of a product that has no click & collect stock completes its
 * inventory. That is what makes the ticket's "stock > 0 pour LS, stock : – pour C&C" cases turn
 * the whole tile green off a single entry.
 */
export function areAllDeclaredFridgesCounted(productStock) {
	const countedLibreService = isFridgeCounted(productStock, STOCK_TYPES.LIBRE_SERVICE)
	const countedClickAndCollect = isFridgeCounted(productStock, STOCK_TYPES.CLICK_AND_COLLECT)

	// Nothing counted at all is not "all counted", however few fridges the product declares.
	if (!countedLibreService && !countedClickAndCollect) return false

	return (!isStockDeclared(productStock.volumeLibreService) || countedLibreService)
		&& (!isStockDeclared(productStock.volume) || countedClickAndCollect)
}

export function handleLastInventory(productStock) {
	let checked = false
	let tooltipText = ""
	// KFC-352: who did the last inventory and when, without the volume that opens `toolTip`.
	// The volume moves to the stock cells at the bottom of the tile, which carry their own
	// tooltip, so the top of the tile is left with the authorship alone.
	let lastInventoryLabel = ""
	let zero =  false
	let retailZero = false
	let clickAndCollectZero = false

	const inventories = productStock.events.filter(elm => elm.type === "INVENTORY")

	if (inventories.length > 0) {
		const lastInventory = inventories[inventories.length - 1]

		const date = moment(lastInventory.date).format("DD/MM HH:mm")
		//@TODO : some inventory don't have user
		tooltipText = `${lastInventory.data.newVolume} - ${lastInventory.user?.username ?? "unknown"} - ${date}`
		checked = isInventoryFresh(lastInventory)

		// KFC-352: a tile reads as empty only when every fridge it shows is empty. Before the
		// two-fridge split there was a single stock, so `volume <= 0` said it all; now a
		// product can be out of click & collect while the self-service fridge still has some.
		//
		// The threshold stays `<= 0` rather than the prototype's `=== 0`: negative volumes
		// exist in the data, and reading them as "not empty" would be worse than greying them.
		//
		// KFC-285: the two fridges are judged the same way. `volume <= 0` alone read an
		// undeclared click & collect as "not empty" — `undefined <= 0` is false — so a product
		// with no click & collect stock, counted empty in self-service, stayed white instead of
		// turning grey. That is the ticket's T-6 and U-8.
	}

	// KFC-351 : ces trois drapeaux se calculent hors du bloc ci-dessus. Ils decrivent l'etat des
	// stocks, pas l'historique : un frigo a zero est vide, qu'on l'ait compte ou non. Les laisser
	// a l'interieur du `if` les figeait a `false` sur un produit sans evenement d'inventaire — ce
	// qui n'etait pas atteignable avant ce ticket, seul un inventaire pouvant mettre un frigo a
	// zero, mais un transfert le peut desormais, et la tuile restait blanche en vision simple.
	retailZero = !isStockDeclared(productStock.volumeLibreService) || productStock.volumeLibreService <= 0
	clickAndCollectZero = !isStockDeclared(productStock.volume) || productStock.volume <= 0
	zero = clickAndCollectZero && retailZero
	lastInventoryLabel = inventoryLabel(lastInventoryOf(productStock), zero)

	productStock.zero = zero
	// Per-fridge, because the single-fridge views judge emptiness on the fridge they show.
	productStock.retailZero = retailZero
	productStock.clickAndCollectZero = clickAndCollectZero
	const dlcRemain = moment.duration(moment.utc(productStock.dlc).diff(moment.utc().startOf("day"))).asSeconds()

	if (undefined === productStock.uniqueCode) productStock.uniqueCode = "NC"
	productStock.display = (["DRINK", "SNACK", "YAOURT"].includes(productStock.productType))?true:(dlcRemain >= 0)
	productStock.toolTip = tooltipText
	productStock.lastInventoryLabel = lastInventoryLabel
	productStock.checked = checked

	// KFC-285: which fridges have been counted, so the tile can colour each cell on its own.
	// Derived from the events rather than stored, exactly as KFC-191 derives a stock type: one
	// source of truth, nothing that can drift.
	productStock.countedLibreService = isFridgeCounted(productStock, STOCK_TYPES.LIBRE_SERVICE)
	productStock.countedClickAndCollect = isFridgeCounted(productStock, STOCK_TYPES.CLICK_AND_COLLECT)

	// The hover text has to follow the view the same way the colour does. A single-fridge view
	// shows one fridge, so it must not name a count taken on the other one — counting click &
	// collect left the self-service tile claiming an inventory nobody did.
	productStock.lastInventoryLabelLibreService = inventoryLabel(lastInventoryFor(productStock, STOCK_TYPES.LIBRE_SERVICE), retailZero)
	productStock.lastInventoryLabelClickAndCollect = inventoryLabel(lastInventoryFor(productStock, STOCK_TYPES.CLICK_AND_COLLECT), clickAndCollectZero)
	productStock.allDeclaredCounted = areAllDeclaredFridgesCounted(productStock)

	return productStock
}

/**
 * The move between the two fridges that a recount reveals, if any.
 *
 * Recounting within the freshness delay with an unchanged total and a different split means
 * units were moved, not lost or found: the inventory then records that move as a transfer before
 * the counts themselves. Anything else is a plain inventory.
 *
 * `counts` carries only the fridges that were counted; a fridge left out keeps its stock.
 * Returns the transfer's `data`, shaped like the one the transfer tab writes, or null.
 */
export function inventoryTransfer(productStock, counts, now = moment.utc()) {
	const counted = value => value !== undefined && value !== null && "" !== value
	const before = {
		libreService: isStockDeclared(productStock.volumeLibreService) ? parseInt(productStock.volumeLibreService) : 0,
		clickAndCollect: isStockDeclared(productStock.volume) ? parseInt(productStock.volume) : 0
	}
	const after = {
		libreService: counted(counts.libreService) ? parseInt(counts.libreService) : before.libreService,
		clickAndCollect: counted(counts.clickAndCollect) ? parseInt(counts.clickAndCollect) : before.clickAndCollect
	}

	const recentInventory = (productStock.events || [])
		.some(event => "INVENTORY" === event.type && isInventoryFresh(event, now))
	const sameTotal = before.libreService + before.clickAndCollect === after.libreService + after.clickAndCollect
	const moved = after.libreService - before.libreService

	if (!recentInventory || !sameTotal || 0 === moved) return null

	const toLibreService = moved > 0
	const from = toLibreService ? "clickAndCollect" : "libreService"

	return {
		quantity: Math.abs(moved),
		from: toLibreService ? STOCK_TYPES.CLICK_AND_COLLECT : STOCK_TYPES.LIBRE_SERVICE,
		to: toLibreService ? STOCK_TYPES.LIBRE_SERVICE : STOCK_TYPES.CLICK_AND_COLLECT,
		oldVolume: before[from],
		newVolume: after[from]
	}
}

/**
 * A transfer event as FoodCheri keeps it: who, when, and the move itself. FoodCheri has users of
 * its own, so the author travels as an email rather than as a KFC pointer.
 */
export function transferAction(event) {
	const user = event.user
	const userEmail = user && "function" === typeof user.get
		? user.get("username") || user.get("email")
		: user && (user.username || user.email)

	return {
		...event.data,
		date: event.date,
		userEmail: userEmail || null
	}
}
